// @flow

import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { get } from 'lodash';
import type { Match, History } from 'react-router-dom';
import { Mutation } from 'react-apollo';
import { components, elements, form, fonts, colors } from '@peachjar/components';
import { css } from 'react-emotion';

import { formatCamelCaseSentence, matchUrlJoin } from '../../../utils';
import Radio from '../components/Radio';
import NcesErrorMessages from '../components/NcesErrorMessages';
import {
  MAX_NUMBER_OF_SCHOOLS_ALLOWED_WITHOUT_PROPOSAL,
  PEACHJAR_CONTACT_NUMBER,
} from '../../../constants';
import SchoolRegistrationButton from '../components/SchoolRegistrationButton';
import CharterSchoolRadios from '../components/CharterSchoolRadios';
import ACCOUNT_CREATION_MUTATION from '../gql/createRegistration.mutation';

const { ProximaNova } = fonts;
const { slate, jungle } = colors;

const { TextInput } = components;

const { Paragraph } = elements.typography;

const styles = {
  inputPassword: {
    fontFamily: ProximaNova,
    marginTop: '16px',
  },
  buttonWrapper: {
    display: 'flex',
    marginLeft: '-5px',
    marginTop: '2rem',
  },
  tempLink: {
    color: jungle, // NOTE: Make sure hyperlinks are Jungle
    textDecoration: 'none',
  },
  circle: {
    width: 8,
    height: 8,
    borderRadius: 8 / 2,
    padding: 2,
    marginLeft: 4,
    marginRight: 8,
  },
  itemThing: {
    alignItems: 'center',
    margin: 6,
    color: slate,
    fontSize: '13px',
  },
  link: css`
     {
      color: ${jungle} !important;
      font-size: 0.8rem;
    }
  `,
};

type Props = {|
  +additionalFields: Array<{ id: string, label: string }>,
  +checkNces: () => void,
  +client: Object,
  +didEnterCorrectNcesIdLength: boolean,
  +didEnterDistrictIdInsteadOfSchoolId: boolean,
  +didEnterSchoolIdInsteadOfDistrictId: boolean,
  +didExceedAllowedSchoolVariance: boolean,
  +eligibility: {
    isEligible?: boolean,
    isEligibleAsIndependentSchool?: boolean,
  },
  +errors: { [string]: string },
  +handleChange: Function,
  +handleCharterSchoolRadioSelection: () => void,
  +handleBlur: Function,
  +handleSubmit: Function,
  +history: History,
  +isCharterSchool: boolean,
  +isCharterSchoolProceeding: boolean,
  +isSoloSchoolInLargerDistrict: boolean,
  +isSubmitting: boolean,
  +match: Match,
  +nextPage: () => void,
  +numberOfSchoolsInt: number,
  +numSchoolsAssociatedToNcesId: number,
  +orgNotFound: boolean,
  +previousPage: () => void,
  +sendFormDataToDatabase: () => void,
  +setFieldTouched: Function,
  +setFieldValue: Function,
  +touched: { [string]: boolean },
  +values: { [string]: string },
|};

const SchoolRegistrationForms = ({
  additionalFields,
  checkNces,
  client,
  didEnterCorrectNcesIdLength,
  didEnterDistrictIdInsteadOfSchoolId,
  didEnterSchoolIdInsteadOfDistrictId,
  didExceedAllowedSchoolVariance,
  eligibility,
  errors,
  handleChange,
  handleCharterSchoolRadioSelection,
  handleBlur,
  handleSubmit,
  history,
  isCharterSchool,
  isCharterSchoolProceeding,
  isSoloSchoolInLargerDistrict,
  isSubmitting,
  match,
  nextPage,
  numberOfSchoolsInt,
  numSchoolsAssociatedToNcesId,
  orgNotFound,
  previousPage,
  sendFormDataToDatabase,
  setFieldTouched,
  setFieldValue,
  touched,
  values,
}: Props) => {
  const {
    districtName,
    contactName,
    ncesId,
    contactPhoneNumber,
    contactEmailAddress,
  } = values;

  return (
    <form>
      <div style={styles.inputPassword}>
        <TextInput
          error={errors.contactName && touched.contactName}
          errorText={formatCamelCaseSentence(errors.contactName)}
          fullWidth
          inputProps={{
            'data-testid': 'field-contactName',
          }}
          name="contactName"
          label="Contact Name"
          value={contactName}
          onChange={handleChange}
          onBlur={handleBlur}
          required
        />
      </div>

      <div style={styles.inputPassword}>
        <TextInput
          error={errors.contactPhoneNumber && touched.contactPhoneNumber}
          errorText={formatCamelCaseSentence(errors.contactPhoneNumber)}
          fullWidth
          inputProps={{
            'data-testid': 'field-contactPhoneNumber',
          }}
          name="contactPhoneNumber"
          label="Contact Phone Number"
          value={contactPhoneNumber}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="i.e. (xxx) xxx-xxxx"
          required
        />
      </div>

      {numberOfSchoolsInt > MAX_NUMBER_OF_SCHOOLS_ALLOWED_WITHOUT_PROPOSAL && (
        <div style={styles.inputPassword}>
          <TextInput
            fullWidth
            error={errors.contactEmailAddress && touched.contactEmailAddress}
            errorText={formatCamelCaseSentence(errors.contactEmailAddress)}
            inputProps={{
              'data-testid': 'field-contactEmailAddress',
            }}
            name="contactEmailAddress"
            label="Contact Email Address"
            value={contactEmailAddress}
            onChange={handleChange}
            onBlur={handleBlur}
            required
          />
        </div>
      )}

      <div>
        <div style={styles.inputPassword}>
          <TextInput
            fullWidth
            error={errors.ncesId && touched.ncesId}
            errorText={formatCamelCaseSentence(errors.ncesId)}
            inputProps={{ 'data-testid': 'field-ncesId' }}
            name="ncesId"
            label={`${numberOfSchoolsInt > 1 ? 'District' : 'School'} NCES ID`}
            value={ncesId}
            onChange={e => {
              handleChange(e);
              checkNces(
                client,
                e.currentTarget.value,
                numberOfSchoolsInt,
                setFieldValue,
                setFieldTouched
              );
            }}
            onBlur={e => {
              handleBlur(e);
              checkNces(
                client,
                e.currentTarget.value,
                numberOfSchoolsInt,
                setFieldValue,
                setFieldTouched
              );
            }}
            required
          />
        </div>
        <div className="pt-1 layout-row layout-align-space-between">
          <a
            rel="noopener noreferrer"
            href={numberOfSchoolsInt === 1 ? "https://nces.ed.gov/globallocator/" : "https://nces.ed.gov/ccd/districtsearch/"}
            className={styles.link}
            target="_blank"
            data-testid="link-idFinder"
          >
            ID Finder
          </a>
          <span
            className={css({
              fontSize: '0.8rem',
            })}
          >
            Don&#39;t have one? Call us at {PEACHJAR_CONTACT_NUMBER}
          </span>
        </div>
      </div>

      <NcesErrorMessages
        didEnterCorrectNcesIdLength={didEnterCorrectNcesIdLength}
        numSchoolsAssociatedToNcesId={numSchoolsAssociatedToNcesId}
        numberOfSchoolsInt={numberOfSchoolsInt}
        didExceedAllowedSchoolVariance={didExceedAllowedSchoolVariance}
        previousPage={previousPage}
        eligibility={eligibility}
        orgNotFound={orgNotFound}
        isSoloSchoolInLargerDistrict={isSoloSchoolInLargerDistrict}
        didEnterSchoolIdInsteadOfDistrictId={
          didEnterSchoolIdInsteadOfDistrictId
        }
        didEnterDistrictIdInsteadOfSchoolId={
          didEnterDistrictIdInsteadOfSchoolId
        }
        isCharterSchool={isCharterSchool}
      />

      {additionalFields.map(options => {
        const { id, label } = options;

        // we want to hide this field if it's a single school and
        // does not have a district. We will reveal it once we know
        // there is a district
        if (
          id === 'districtName' &&
          numberOfSchoolsInt === 1 &&
          !districtName
        ) {
          return null;
        }

        return (
          <div style={styles.inputPassword} key={id}>
            <TextInput
              fullWidth
              id={id}
              error={errors[id] && touched[id]}
              errorText={formatCamelCaseSentence(errors[id])}
              inputProps={{
                'data-testid': `field-${id}`,
              }}
              name={id}
              label={label}
              value={values[id]}
              onChange={handleChange}
              // onChange={e => this.handleFormInput(e, id)}
              onBlur={handleBlur}
              placeholder={
                id === 'physicalAddress' ? 'i.e. street, city, state, zip' : ''
              }
              required
            />
          </div>
        );
      })}

      {isCharterSchool === true &&
        get(eligibility, 'isEligibleAsIndependentSchool') && (
          <CharterSchoolRadios
            isCharterSchoolProceeding={isCharterSchoolProceeding}
            handleCharterSchoolRadioSelection={
              handleCharterSchoolRadioSelection
            }
          />
        )}

      <Mutation mutation={ACCOUNT_CREATION_MUTATION}>
        {(createAccount, { loading, networkStatus, error }) => {
          const mutationValues = Object.assign(values, {
            asIndependentSchool: !!isCharterSchoolProceeding,
          });
          let onclick;
          let buttonText;

          if (
            numberOfSchoolsInt > MAX_NUMBER_OF_SCHOOLS_ALLOWED_WITHOUT_PROPOSAL
          ) {
            // RFP flow
            onclick = e => {
              handleSubmit(e);
              sendFormDataToDatabase(
                createAccount,
                () => {
                  history.push(matchUrlJoin(match.url, '/rfp'))
                },
                mutationValues
                );
            };
            buttonText = 'Submit';
          } else {
            // non-RFP flow
            onclick = e => {
              handleSubmit(e);
              sendFormDataToDatabase(
                createAccount,
                () =>
                  history.push(matchUrlJoin(match.url, '/user-registration')),
                mutationValues
              );
            };
            buttonText = 'Continue';
          }

          return (
            <SchoolRegistrationButton
              buttonText={buttonText}
              touched={touched}
              errors={errors}
              orgNotFound={orgNotFound}
              loading={loading}
              isSoloSchoolInLargerDistrict={isSoloSchoolInLargerDistrict}
              didEnterSchoolIdInsteadOfDistrictId={
                didEnterSchoolIdInsteadOfDistrictId
              }
              didEnterDistrictIdInsteadOfSchoolId={
                didEnterDistrictIdInsteadOfSchoolId
              }
              didExceedAllowedSchoolVariance={didExceedAllowedSchoolVariance}
              isSubmitting={isSubmitting}
              eligibility={eligibility}
              numberOfSchoolsInt={numberOfSchoolsInt}
              isCharterSchool={isCharterSchool}
              isCharterSchoolProceeding={isCharterSchoolProceeding}
              onclick={onclick}
              networkStatus={networkStatus}
            />
          );
        }}
      </Mutation>
    </form>
  );
};

export default SchoolRegistrationForms;



// WEBPACK FOOTER //
// ./src/Onboarding/Registration/onboarding/containers/SchoolRegistration/containers/SchoolRegistrationForms.jsx